import { useCallback, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { useHistory, useLocation } from 'react-router-dom';
import { z } from 'zod';

import { parse, stringify } from 'util/urlHelpers';

const preserveParamOrder = (
  existingParams: Record<string, any>,
  newParams: Record<string, any>,
  defaultParams: Record<string, any>
): Record<string, any> => {
  const originalOrder = Object.keys(existingParams);
  const updatedKeys = new Set(Object.keys(newParams));
  const processedKeys = new Set<string>();

  const result: Record<string, any> = {};

  // 1. First, add all existing params in their EXACT original URL order
  // EXCEPT for ones being updated (which will go to the end)
  originalOrder.forEach(key => {
    if (
      !updatedKeys.has(key) &&
      existingParams[key] !== undefined &&
      existingParams[key] !== null
    ) {
      result[key] = existingParams[key];
      processedKeys.add(key);
    }
  });

  // 2. Add any existing params that weren't in the original URL order
  // (but still not the ones being updated)
  Object.keys(existingParams).forEach(key => {
    if (
      !processedKeys.has(key) &&
      !updatedKeys.has(key) &&
      existingParams[key] !== undefined &&
      existingParams[key] !== null
    ) {
      result[key] = existingParams[key];
      processedKeys.add(key);
    }
  });

  // 3. Add all updated params at the end
  Object.keys(newParams).forEach(key => {
    if (newParams[key] !== undefined && newParams[key] !== null) {
      result[key] = newParams[key];
      processedKeys.add(key);
    }
  });

  // 4. Add any default params that aren't already present
  Object.keys(defaultParams).forEach(key => {
    if (
      !processedKeys.has(key) &&
      defaultParams[key] !== undefined &&
      defaultParams[key] !== null
    ) {
      result[key] = defaultParams[key];
    }
  });

  return result;
};

export function parseSearchQueryParams<T extends Record<string, any>>(
  search: string,
  defaultParams: T,
  zodSchema?: z.ZodType<T>
) {
  const urlQueryParams = parse(search, { sort: false });

  const params = { ...defaultParams, ...urlQueryParams };

  if (zodSchema) {
    const res = zodSchema.safeParse(params);

    if (!res.success) {
      console.error(res.error);
      toast.error('Invalid search params');
      return params;
    }

    return res.data;
  }

  return params;
}

export function useQueryParams<T extends Record<string, any>>(
  defaultParams: T,
  zodSchema?: z.ZodType<T>
) {
  const history = useHistory();
  const location = useLocation();

  const paramsWithDefaultValues = useMemo(
    () => parseSearchQueryParams(location.search, defaultParams, zodSchema),
    [defaultParams, location.search, zodSchema]
  );

  const urlParams = useMemo(() => parse(location.search, { sort: false }), [location.search]);

  const generateNewParams = useCallback(
    (params: Partial<T>, clearOther = false) => {
      if (clearOther) {
        return stringify(params, { sort: false });
      }

      const orderedParams = preserveParamOrder(urlParams, params, defaultParams);
      return stringify(orderedParams, { sort: false });
    },
    [urlParams, defaultParams]
  );

  const setQueryParams = useCallback(
    (params: Partial<T>, options: { clearOther?: boolean } = {}) => {
      const newSearch = generateNewParams(params, options.clearOther);

      if (newSearch !== location.search) {
        history.push({ search: newSearch });
      }
    },
    [generateNewParams, history, location.search]
  );

  return [paramsWithDefaultValues, setQueryParams, generateNewParams] as const;
}
